/**
 * ViewCube + compass overlay for the façade viewer.
 * Renders its own tiny scene in a corner canvas and mirrors the main camera's
 * orientation. Clicking a face / edge / corner reports the requested view
 * direction in the model's world coordinate system (Z-up, +Y = North).
 */
import * as THREE from "three";

const FACES = [
  { label: "TOP", normal: [0, 0, 1], up: [0, 1, 0] },
  { label: "BOTTOM", normal: [0, 0, -1], up: [0, -1, 0] },
  { label: "FRONT", normal: [0, -1, 0], up: [0, 0, 1] },
  { label: "BACK", normal: [0, 1, 0], up: [0, 0, 1] },
  { label: "LEFT", normal: [-1, 0, 0], up: [0, 0, 1] },
  { label: "RIGHT", normal: [1, 0, 0], up: [0, 0, 1] },
];

const COMPASS = [
  { label: "N", angle: 0 },
  { label: "E", angle: -Math.PI / 2 },
  { label: "S", angle: Math.PI },
  { label: "W", angle: Math.PI / 2 },
];

const SIZE = 118;
const SIZE_COMPACT = 86;
const EDGE_BAND = 0.34; // |coord| above this counts as "on that side"

function labelTexture(text, { size = 128, font = 34, color = "#334155" } = {}) {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = color;
  ctx.font = `600 ${font}px "Helvetica Neue", Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, size / 2, size / 2 + 2);
  const texture = new THREE.CanvasTexture(canvas);
  texture.anisotropy = 4;
  return texture;
}

export class ViewCube {
  constructor(container, mainCamera, onPick) {
    this.container = container;
    this.mainCamera = mainCamera;
    this.onPick = onPick;

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.canvas = this.renderer.domElement;
    this.canvas.setAttribute("data-testid", "viewer-viewcube");
    container.appendChild(this.canvas);
    this.layout();

    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(-1.62, 1.62, 1.62, -1.62, 0.1, 100);
    this.camera.up.set(0, 0, 1);

    this.scene.add(new THREE.HemisphereLight(0xffffff, 0xd6dee9, 1.4));

    this.cubeGroup = new THREE.Group();
    this.scene.add(this.cubeGroup);

    // Solid body so faces are opaque and back faces stay hidden.
    const body = new THREE.Mesh(
      new THREE.BoxGeometry(0.985, 0.985, 0.985),
      new THREE.MeshBasicMaterial({ color: 0xfdfefe })
    );
    this.cubeGroup.add(body);

    this._textures = [];
    FACES.forEach((face) => {
      const normal = new THREE.Vector3(...face.normal);
      const up = new THREE.Vector3(...face.up);
      const right = new THREE.Vector3().crossVectors(up, normal);
      const texture = labelTexture(face.label, { size: 160, font: face.label.length > 4 ? 34 : 42 });
      this._textures.push(texture);
      const plane = new THREE.Mesh(
        new THREE.PlaneGeometry(1, 1),
        new THREE.MeshBasicMaterial({ map: texture, transparent: true, color: 0xffffff })
      );
      plane.setRotationFromMatrix(new THREE.Matrix4().makeBasis(right, up, normal));
      plane.position.copy(normal).multiplyScalar(0.5005);
      this.cubeGroup.add(plane);
    });

    const edges = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1)),
      new THREE.LineBasicMaterial({ color: 0x94a3b8 })
    );
    this.cubeGroup.add(edges);

    // Hover highlight: a slab/box covering the hovered face, edge or corner.
    this.highlight = new THREE.Mesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({ color: 0x06b6d4, transparent: true, opacity: 0.28, depthWrite: false })
    );
    this.highlight.visible = false;
    this.cubeGroup.add(this.highlight);

    // Compass ring lies in the model XY plane, under the cube. +Y = North.
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(1.02, 1.1, 64),
      new THREE.MeshBasicMaterial({ color: 0xcbd5e1, side: THREE.DoubleSide, transparent: true, opacity: 0.9 })
    );
    ring.position.z = -0.62;
    this.cubeGroup.add(ring);

    COMPASS.forEach(({ label, angle }) => {
      const texture = labelTexture(label, { size: 96, font: 66, color: label === "N" ? "#0E7490" : "#64748B" });
      this._textures.push(texture);
      const plane = new THREE.Mesh(
        new THREE.PlaneGeometry(0.5, 0.5),
        new THREE.MeshBasicMaterial({ map: texture, transparent: true, side: THREE.DoubleSide })
      );
      plane.position.set(Math.sin(-angle) * 1.4, Math.cos(-angle) * 1.4, -0.62);
      this.cubeGroup.add(plane);
    });

    this.pickTarget = new THREE.Mesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({ visible: false })
    );
    this.scene.add(this.pickTarget);

    this.raycaster = new THREE.Raycaster();
    this._onMove = (event) => this._handleMove(event);
    this._onDown = (event) => {
      this._down = { x: event.clientX, y: event.clientY };
    };
    this._onUp = (event) => this._handleUp(event);
    this._onLeave = () => {
      this.highlight.visible = false;
    };
    this.canvas.addEventListener("pointermove", this._onMove);
    this.canvas.addEventListener("pointerdown", this._onDown);
    this.canvas.addEventListener("pointerup", this._onUp);
    this.canvas.addEventListener("pointerleave", this._onLeave);
  }

  /** Small screens: shrink the cube and drop it below the floating toolbar. */
  layout() {
    const compact = (this.container?.clientWidth || window.innerWidth) < 1024;
    const size = compact ? SIZE_COMPACT : SIZE;
    const top = compact ? 116 : 12;
    this.renderer.setSize(size, size, false);
    this.canvas.style.cssText = `position:absolute;top:${top}px;right:10px;width:${size}px;height:${size}px;z-index:16;cursor:pointer;touch-action:none`;
  }

  /** Region under the pointer as a signed direction vector, or null. */
  _pick(event) {
    const rect = this.canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
    this.raycaster.setFromCamera(ndc, this.camera);
    const hit = this.raycaster.intersectObject(this.pickTarget, false)[0];
    if (!hit) return null;
    const local = this.pickTarget.worldToLocal(hit.point.clone());
    const dir = new THREE.Vector3(
      Math.abs(local.x) > EDGE_BAND ? Math.sign(local.x) : 0,
      Math.abs(local.y) > EDGE_BAND ? Math.sign(local.y) : 0,
      Math.abs(local.z) > EDGE_BAND ? Math.sign(local.z) : 0
    );
    return dir.lengthSq() ? dir : null;
  }

  _handleMove(event) {
    const dir = this._pick(event);
    if (!dir) {
      this.highlight.visible = false;
      return;
    }
    const thin = 0.14;
    const wide = 1 - 2 * EDGE_BAND + 0.02;
    ["x", "y", "z"].forEach((axis) => {
      const sign = dir[axis];
      this.highlight.scale[axis] = sign ? thin : wide;
      this.highlight.position[axis] = sign ? sign * (0.5 - thin / 2 + 0.005) : 0;
    });
    this.highlight.visible = true;
  }

  _handleUp(event) {
    const start = this._down;
    this._down = null;
    if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 4) return;
    const dir = this._pick(event);
    if (dir) this.onPick?.(dir.normalize());
  }

  /** Mirror the main camera's screen orientation each frame. */
  update() {
    const matrix = this.mainCamera.matrixWorld.elements;
    const forward = new THREE.Vector3(matrix[8], matrix[9], matrix[10]).normalize();
    const up = new THREE.Vector3(matrix[4], matrix[5], matrix[6]).normalize();
    this.camera.position.copy(forward.multiplyScalar(10));
    this.camera.up.copy(up);
    this.camera.lookAt(0, 0, 0);
    this.renderer.render(this.scene, this.camera);
  }

  dispose() {
    this.canvas.removeEventListener("pointermove", this._onMove);
    this.canvas.removeEventListener("pointerdown", this._onDown);
    this.canvas.removeEventListener("pointerup", this._onUp);
    this.canvas.removeEventListener("pointerleave", this._onLeave);
    this._textures.forEach((texture) => texture.dispose());
    this.renderer.dispose();
    this.canvas.remove();
  }
}
